import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Check for existing authentication on mount
  useEffect(() => {
    console.log('🔍 AuthContext: Checking localStorage on mount...');
    const storedToken = localStorage.getItem('classtraks_token');
    const storedUser = localStorage.getItem('classtraks_user');
    
    console.log('🔍 AuthContext: storedToken =', !!storedToken);
    console.log('🔍 AuthContext: storedUser =', !!storedUser);

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log('🔍 AuthContext: Setting authenticated = true');
        setToken(storedToken);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('🔍 AuthContext: Error parsing stored user data:', error);
        // Clear invalid data without navigating
        localStorage.removeItem('classtraks_token');
        localStorage.removeItem('classtraks_user');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      }
    } else {
      console.log('🔍 AuthContext: No valid auth data found, setting authenticated = false');
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  }, []);

  // Listen for storage changes (useful for multiple tabs)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedToken = localStorage.getItem('classtraks_token');
      const storedUser = localStorage.getItem('classtraks_user');
      
      if (!storedToken || !storedUser) {
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Store authentication data
      localStorage.setItem('classtraks_token', data.token);
      localStorage.setItem('classtraks_user', JSON.stringify(data.user));
      
      setToken(data.token);
      setUser(data.user);
      setIsAuthenticated(true);

      console.log('✅ Login successful, user authenticated:', data.user.email);
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      // Store authentication data
      localStorage.setItem('classtraks_token', data.token);
      localStorage.setItem('classtraks_user', JSON.stringify(data.user));
      
      setToken(data.token);
      setUser(data.user);
      setIsAuthenticated(true);

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('classtraks_token');
    localStorage.removeItem('classtraks_user');
    // Clear ALL cached user-specific data to prevent showing wrong data on next login
    localStorage.removeItem('classtraks_students');
    localStorage.removeItem('classtraks_classes');
    localStorage.removeItem('classtraks_projects');
    localStorage.removeItem('classtraks_enrollments');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    // Navigation handled by ProtectedRoute redirect
  };

  // Update user data
  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem('classtraks_user', JSON.stringify(updatedUser));
  };

  // Get authorization headers for API requests
  const getAuthHeaders = () => {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // Check if user has specific role
  const hasRole = (requiredRole) => {
    return user?.role === requiredRole;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    getAuthHeaders,
    hasRole,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;